import actorResourceNames from './Data/ActorResourceNames';
import inGameIconNames from './Data/InGameIconNames';
import fonts from './Data/Fonts';
import languages from './Data/Languages';
import memoizeOne from 'memoize-one';
import meshEffectNams from './Data/MeshEffectNames';
import musicNames from './Data/MusicNames';
import path from 'path';
import resourceTypeNames from './Data/ResourceTypeNames';

import React from 'react';
import ReactTable, { ReactTableDefaults } from "react-table";
import TypeDefaults from './Data/Defaults';
import PropTypes from 'prop-types';

import simpleFormat from './SimpleFormat';

import { assignIfDifferent } from './PredictableStore';
import { contextMenu } from 'react-contexify';

import { Button, Modal } from 'react-bootstrap';
import { DataEditorData, UndoableDataEditorData } from './Proto/dcei_editor_data_proto';
import { DataFile } from './Proto/dcei_engine_data_proto';
import { FieldDescriptor } from './Helper/Descriptor';
import { LazySelect } from './CellSelect';
import { MdClear, MdContentCopy, MdPlayArrow, MdPlayCircleOutline, MdPauseCircleOutline } from 'react-icons/md';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReactMarkdown from "react-markdown";
import Tooltip from "react-png-tooltip";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import 'react-contexify/dist/ReactContexify.min.css';
import './style.scss';

async function fetchGitlabFile(file) {
    const privateToken = "aKjd5mLJdHyme5b6jejS";
    const response = await fetch(`https://gitlab.com/api/v4/projects/22117357/repository/files/${file}/raw?ref=master&private_token=${privateToken}`);
    return await response.text();
}

/**
 * @type {{string: Promise<string>}}
 */
const wikiFiles = {};

async function fetchWikiFile(file) {
    if (!wikiFiles[file]) {
        wikiFiles[file] = fetchGitlabFile(file);
    }
    return await wikiFiles[file];
}

async function fetchWikiContent(wikiPath) {
    const file = `Data-${wikiPath[0]}.md`;
    const content = await fetchWikiFile(file);
    const title = wikiPath[wikiPath.length - 1];

    const lines = content.split("\n");
    for (let i = 0; i < lines.length; i++) {
        const left = lines[i].replace(/\s/g, "").toLowerCase();
        const right = '##' + title.replace(/\s/g, "").toLowerCase();
        if (left === right) {
            let content = lines[i] + '\n';
            for (let j = i + 1; j < lines.length; j++) {
                if (lines[j] == null) {
                    break;
                }
                if (lines[j].startsWith("#")) {
                    break;
                }
                content += (lines[j] + "\n");
            }
            return content;
        }
    }
    return 'Not found.';
}

function getEditorAssetPath() {
    if (!window.require) {
        return null;
    }
    const electron = window.require('electron');
    const app = electron.remote.app;
    let dir_path = app.getAppPath();
    if (dir_path.endsWith('.asar')) {
        dir_path = dir_path.substr(0, dir_path.lastIndexOf('\\'));
    }
    dir_path = dir_path.split('\\').join('/');
    return dir_path;
}

function wikiTitleName(name) {
    let result = "";
    let forceUpper = true;
    for (let c of name) {
        if (c == '_') {
            result += " ";
            forceUpper = true;
        } else if (c >= "A" && c <= "Z") {
            forceUpper = false;
            result += " ";
            result += c;
        } else if (forceUpper && c >= 'a' && c <= 'z') {
            forceUpper = false;
            result += " ";
            result += c.toUpperCase();
        } else {
            result += c;
        }
    }
    return result.trim();
}

function wikiTitleNameForField(field) {
    return wikiTitleName(field.name);
}

const editor_asset_path = getEditorAssetPath();
const audio_server = 'http://localhost:57749/';

export function prettyName(name) {
    let result = "";
    let forceUpper = true;
    for (let c of name) {
        if (c >= "A" && c <= "Z") {
            forceUpper = false;
            result += " ";
            result += c;
        } else if (forceUpper && c >= 'a' && c <= 'z') {
            forceUpper = false;
            result += " ";
            result += c.toUpperCase();
        } else if (c == ".") {
            result += " => ";
        } else {
            result += c;
        }
    }
    return result.trim();
}

function prettyProtoName(name) {
    let result = "";
    let forceUpper = true;
    for (let c of name) {
        if (c == '_') {
            result += " ";
            forceUpper = true;
        } else if (c >= "A" && c <= "Z") {
            forceUpper = false;
            result += " ";
            result += c;
        } else if (forceUpper && c >= 'a' && c <= 'z') {
            forceUpper = false;
            result += " ";
            result += c.toUpperCase();
        } else if (c == ".") {
            result += " => ";
        } else {
            result += c;
        }
    }
    return result.trim();
}

function getParentFieldName(itemSetName) {
    switch (itemSetName) {
        case "units":
            return "baseUnit";
        default:
            return "parent";
    }
}

export function itemListSort(a, b) {
    a = a.replace("_", ".");
    b = b.replace("_", ".");
    return a.localeCompare(b);
}

function postData(url, data) {
    return fetch(url, {
        body: JSON.stringify(data),
        headers: {
            'user-agent': 'Unity_Agent',
            'content-type': 'application/json'
        },
        method: 'POST'
    });
}

function playAudio(e, audioName) {
    e.preventDefault();
    e.stopPropagation();
    postData(audio_server, { AssetType: 'Sound', AssetName: audioName, Command: 'Play' });
}

function stopAudio(e, audioName) {
    e.preventDefault();
    e.stopPropagation();
    postData(audio_server, { AssetType: 'Sound', AssetName: audioName, Command: 'Stop' });
}

function previewAsset(e, assetType, assetName) {
    e.preventDefault();
    e.stopPropagation();
    postData(audio_server, { AssetType: assetType, AssetName: assetName, Command: 'Select' });
}

export class ProtoContext {
    constructor() {
        this._dataEditorData = DataEditorData.default;
        this._undoableDataEditorData = UndoableDataEditorData.default;
        this.updateModules();

        this._supportedValueDropdowns = {
            DataFieldUpgradePath: (message, field, index) => {
                if (index > 0) {
                    const values = message.selfValue[field.name].slice(0, index);
                    const itemSetName = values[0];
                    const parentItemName = getParentFieldName(itemSetName);
                    if (index > 1) {
                        const proto = this.getProtoWithParent(itemSetName, parentItemName, values[1]);
                        if (!proto) {
                            return { values: [`path ${values.join()} is invalid`], noCache: true };
                        }
                        let item = proto.data;
                        for (let i = 2; i < values.length; i++) {
                            if (item.descriptor !== undefined) {
                                const field_descriptor = item.descriptor.fields.find(f => f.protoName === values[i]);
                                if (field_descriptor) {
                                    item = item[field_descriptor.name];
                                } else {
                                    item = null;
                                }
                            } else {
                                item = item[values[i]];
                            }
                            if (!item) {
                                return { values: [`path ${values.join()} is invalid`], noCache: true };
                            }
                        }
                        if (item.descriptor === undefined) {
                            return { values: Object.keys(item), noCache: true };
                        } else {
                            return { values: item.descriptor.fields.map(f => f.protoName), noCache: true };
                        }
                    } else {
                        return { values: this._getNames(itemSetName, { parentItemName }), noCache: true };
                    }
                } else {
                    return { values: DataFile.descriptor.fields.filter(f => f.isMap).map(f => f.name), noCache: true };
                }
            },
            SupportedAbilityListNames: { field: 'abilities' },
            SupportedAbilityListNamesWithNull: { field: 'abilities', options: { withNull: true } },
            SupportedAbilityNamesWithNull: { field: 'abilities', options: { withNull: true } },
            SupportedActorEventMacroNames: { field: 'actorEventMacros' },
            SupportedActorListNames: { field: 'actors' },
            SupportedActorListNamesWithNull: { field: 'actors', options: { withNull: true } },
            SupportedAreaSearchEffectNames: { field: 'effects', options: { filter: e => e.oneofEffectTypeCase == 4 } },
            SupportedBaseUnitNames: { field: 'units' },
            SupportedBaseUnitNamesWithNull: { field: 'units', options: { withNull: true } },
            SupportedBehaviorNames: { field: 'behaviors' },
            SupportedBehaviorNamesWithNull: { field: 'behaviors', options: { withNull: true } },
            SupportedEffectListNames: { field: 'effects' },
            SupportedEffectListNamesWithNull: { field: 'effects', options: { withNull: true } },
            SupportedEffectNamesWithNull: { field: 'effects', options: { withNull: true } },
            SupportedExpressionNames: { field: 'expressions' },
            SupportedInGameIcons: () => {
                return { values: inGameIconNames };
            },
            Fonts: () => {
                return { values: fonts };
            },
            Languages: () => {
                return { values: languages };
            },
            SupportedMeshEffectNames: () => {
                return { values: meshEffectNams };
            },
            SupportedMoverNames: { field: 'movers' },
            SupportedMoverNamesWithNull: { field: 'movers', options: { withNull: true } },
            SupportedMusicNames: () => {
                return { values: musicNames };
            },
            SupportedResourceTypeNames: () => {
                return { values: resourceTypeNames };
            },
            SupportedSiteOperationNames: { field: 'actors', options: { filter: a => a.oneofActorTypeCase == 5 } },
            SupportedUnitActorNames: {
                field: 'actors', options: {
                    filter: a =>
                        a.oneofActorTypeCase == 2 && a.unitActor.resource.type == "GpuAnimation"
                }
            },
            SupportedSpawnPatternGroupNames: { field: 'spawnPatternGroups' },
            SupportedSpawnPatternListNames: { field: 'spawnPatterns' },
            SupportedDataUpgrades: { field: 'dataUpgrades' },
            SupportedValidatorNames: { field: 'validators' },
            SupportedValidatorNamesWithNull: { field: 'validators', options: { withNull: true } },
            SupportedWaypointNameList: () => {
                if (!this._waypoints.RoutesData) {
                    // Old format.
                    return { values: Object.keys(this._waypoints).sort(), noCache: true };
                }
                return { values: Object.keys(this._waypoints.RoutesData).sort(), noCache: true };
            },
            SupportedWeaponListNames: { field: 'weapons' },
            SupportedWeaponNamesWithNull: { field: 'weapons', options: { withNull: true } },
            SupportedYieldGroupNames: message => {
                return { values: this._getYieldGroupNames(message), noCache: true };
            },
            SupportedModelNameList: message => {
                return { values: this._getSupportedModelNames(message), noCache: true };
            },
        };
    }

    updateModules() {
        // console.log('updating proto context', this);
        this._modules = this._dataEditorData.dependencies.concat([{ name: 'CURRENT', data: this._undoableDataEditorData.data }]);
        this._waypoints = this._dataEditorData.waypoints ? JSON.parse(this._dataEditorData.waypoints) : {};
        this._cachedValueDropdowns = {};
    }

    set dataEditorData(value) {
        this._dataEditorData = value;
        this.updateModules();
    }

    set undoableDataEditorData(value) {
        this._undoableDataEditorData = value;
        this.updateModules();
    }

    getTypeDefault(type) {
        if (!TypeDefaults.has(type)) {
            return null;
        }
        this._getTypeDefaultCache = this._getTypeDefaultCache || new Map();
        if (!this._getTypeDefaultCache.has(type)) {
            const result = new type.WithParent(TypeDefaults.get(type), null);
            result.moduleName = '<Defaults>';
            result.uniqueName = type.name;
            this._getTypeDefaultCache.set(type, result);
        }
        return this._getTypeDefaultCache.get(type);
    }

    _getItemWithParent(itemSetName, parentItemName, name, moduleIndex, visited, cache) {
        const modules = this._modules;

        cache[moduleIndex] = cache[moduleIndex] || {};
        // 'cache' contains values calculated in the current calculation pass (i.e., guranteed to
        // be from the latest values).
        if (cache[moduleIndex][name]) {
            return cache[moduleIndex][name];
        }

        let current = modules[moduleIndex].data[itemSetName][name];
        for (let v of visited) {
            if (v.name === name && v.moduleIndex === moduleIndex) {
                console.error('Data has circular parenting: ', visited.map(v => v.name + '@' + modules[v.moduleIndex].name).concat(name + '@' + modules[moduleIndex].name));
                return this.getTypeDefault(current.constructor);
            }
        }
        visited.push({ name, moduleIndex });

        // Before calculating the value, fetch the cached entry from the last calculation pass.
        // This cached value may be calculated from a out-dated dataset so use it only if all
        // values it depends on are still current.
        const allCache = this._getItemWithParentCache || (this._getItemWithParentCache = {});
        const moduleCache = allCache[moduleIndex] || (allCache[moduleIndex] = {});
        const itemSetCache = moduleCache[itemSetName] || (moduleCache[itemSetName] = {});
        const itemCache = itemSetCache[name] || (itemSetCache[name] = {});

        let parent = this.getTypeDefault(current.constructor);

        // Find it's parent.
        const parentName = current[parentItemName];
        if (parentName) {
            for (let i = modules.length - 1; i >= 0; i--) {
                if (modules[i].data[itemSetName][parentName]) {
                    parent = this._getItemWithParent(itemSetName, parentItemName, parentName, i, visited, cache);
                    break;
                }
            }
        } else {
            // Find it's dependency parent.
            for (let i = moduleIndex - 1; i >= 0; i--) {
                if (modules[i].data[itemSetName][name]) {
                    parent = this._getItemWithParent(itemSetName, parentItemName, name, i, visited, cache);
                    break;
                }
            }
        }

        let result = null;

        if (itemCache.selfValue === current && itemCache.parentValue === parent) {
            // console.log('Using cached value for ', name, modules[moduleIndex].name, itemCache);
            result = itemCache.value;
        } else if (itemCache.value && itemCache.parentValue === parent) {
            result = itemCache.value.update(current, parent);
            result.moduleName = modules[moduleIndex].name;
            result.uniqueName = name;

            itemCache.selfValue = current;
            itemCache.parentValue = parent;
            itemCache.value = result;
            // console.log('Half new cache value for ', name, modules[moduleIndex].name, itemCache);
        } else {
            result = new current.constructor.WithParent(current, parent);
            result.moduleName = modules[moduleIndex].name;
            result.uniqueName = name;

            itemCache.selfValue = current;
            itemCache.parentValue = parent;
            itemCache.value = result;
            // console.log('Calculated new cache value for ', name, modules[moduleIndex].name, itemCache);
        }

        cache[moduleIndex][name] = result;
        visited.pop();

        // console.log('returning proto value', result);

        return result;
    }

    getProtoWithParent(itemSetName, parentItemName, name, containingModule) {
        const modules = this._modules;
        /**
         * @type {string[]}
         */
        const containingModules = [];
        for (let i = modules.length - 1; i >= 0; i--) {
            if (modules[i].data[itemSetName][name]) {
                containingModules.push(modules[i].name);
            }
        }
        for (let i = modules.length - 1; i >= 0; i--) {
            if (modules[i].data[itemSetName][name]) {
                if (containingModules.indexOf(containingModule) !== -1 && containingModule !== modules[i].name) {
                    continue;
                }
                const cache = {};
                const visited = [];
                const result = this._getItemWithParent(itemSetName, parentItemName, name, i, visited, cache);
                return {
                    data: result,
                    inherent: i != modules.length - 1,
                    dispatchPath: ['undoableDataEditorData', 'data', itemSetName, name],
                    containingModule: modules[i].name,
                    containingModules,
                };
            }
        }
        return null;
    }

    /**
     * @returns {string[]}
     */
    getUnitNames() {
        return this.getValueDropdownOptions("SupportedBaseUnitNames");
    }

    /**
     * @returns {string[]}
     */
    getAbilityNames() {
        return this.getValueDropdownOptions("SupportedAbilityListNames");
    }

    /**
     * @returns {string[]}
     */
    getWeaponNames() {
        return this.getValueDropdownOptions("SupportedWeaponListNames");
    }

    /**
     * @returns {string[]}
     */
    getBehaviorNames() {
        return this.getValueDropdownOptions("SupportedBehaviorNames");
    }


    /**
     * @returns {string[]}
     */
    getEffectNames() {
        return this.getValueDropdownOptions("SupportedEffectListNames");
    }

    /**
     * @returns {string[]}
     */
    getAreaSearchEffectNames() {
        return this.getValueDropdownOptions("SupportedAreaSearchEffectNames");
    }

    /**
     * @returns {string[]}
     */
    getExpressionNames() {
        return this.getValueDropdownOptions("SupportedExpressionNames");
    }

    /**
     * @returns {string[]}
     */
    getValidatorNames() {
        return this.getValueDropdownOptions("SupportedValidatorNames");
    }

    /**
     * @returns {string[]}
     */
    getMoverNames() {
        return this.getValueDropdownOptions("SupportedMoverNames");
    }

    /**
     * @returns {string[]}
     */
    getActorNames() {
        return this.getValueDropdownOptions("SupportedActorListNames");
    }

    /**
     * @returns {string[]}
     */
    getUnitActorNames() {
        return this.getValueDropdownOptions("SupportedUnitActorNames");
    }

    /**
     * @returns {string[]}
     */
    getSiteOperationNames() {
        return this.getValueDropdownOptions("SupportedSiteOperationNames");
    }

    /**
     * @returns {string[]}
     */
    getActorEventMacroNames() {
        return this.getValueDropdownOptions("SupportedActorEventMacroNames");
    }

    /**
     * @returns {string[]}
     */
    getDataUpgrades() {
        return this.getValueDropdownOptions("SupportedDataUpgrades");
    }

    /**
     * @returns {string[]}
     */
    getInGameIcons() {
        return this.getValueDropdownOptions("SupportedInGameIcons");
    }

    /**
     * @returns {string[]}
     */
    getFonts() {
        return this.getValueDropdownOptions("Fonts");
    }

    /**
     * @returns {string[]}
     */
    getLanguages() {
        return this.getValueDropdownOptions("Languages");
    }


    /**
     * @returns {string[]}
     */
    getMusicNames() {
        return this.getValueDropdownOptions("SupportedMusicNames");
    }

    /**
     * @returns {string[]}
     */
    getSoundNames() {
        return this.getValueDropdownOptions("SupportedModelNameList", { type: "Sound" });
    }

    /**
   * @returns {string[]}
   */
    getUnitActorNamesWithGpuAnimation() {
        return this.getValueDropdownOptions("SupportedUnitActorNames");
    }

    /**
     * @returns {string[]}
     */
    getSpawnPatternNames() {
        return this.getValueDropdownOptions("SupportedSpawnPatternListNames");
    }

    /**
     * @returns {string[]}
     */
    getSpawnPatternGroupNames() {
        return this.getValueDropdownOptions("SupportedSpawnPatternGroupNames");
    }

    /**
     * @returns {string[]}
     */
    getWaypointNames() {
        return this.getValueDropdownOptions("SupportedWaypointNameList");
    }

    /**
     * @returns {string[]}
     */
    getValueDropdownOptions(name, message, field, index) {
        this._cachedValueDropdowns = this._cachedValueDropdowns || {};
        if (!this._cachedValueDropdowns[name]) {
            const info = this._supportedValueDropdowns[name];
            if (!info) {
                console.error('ValueDropdown not supported', name);
                return [`<Invalid: ValueDropdown ${name} not supported>`];
            }
            let result = null;
            if (typeof info === 'function') {
                const data = info(message, field, index);
                result = data.values;
                if (data.noCache) {
                    return result;
                }
            } else {
                result = this._getNames(info.field, info.options);
            }
            this._cachedValueDropdowns[name] = result;
        }
        return this._cachedValueDropdowns[name];
    }

    getValueDropdownItemSetName(name) {
        const info = this._supportedValueDropdowns[name];
        if (typeof info === 'function') {
            return null;
        }
        return info.field;
    }

    getValueDropdownOptionsWithLabel(name, message, field, index) {
        let names = this.getValueDropdownOptions(name, message, field, index);
        return names.map(function (value) {
            if (editor_asset_path && name == "SupportedModelNameList" && (message.type == "GpuAnimation" || message.type == "ParticleAnimation" || message.type == "PropModel")) {
                const model_thumbnail_path = path.join(editor_asset_path, 'public/editor-assets/ActorModels');
                return {
                    value: value,
                    label: (
                        <div>
                            <div>{value}</div>
                            <div style={{ height: 72, width: 72, backgroundImage: `url('${model_thumbnail_path + "/" + message.type + "/" + value + ".png"}')`, backgroundSize: 'cover', color: 'white' }}></div>
                            <Button style={{ backgroundColor: '#4CAF50' }} onClick={(e) => previewAsset(e, message.type, value)} title="Select"><MdPlayCircleOutline /></Button>
                        </div>
                    )
                };
            }
            else if (editor_asset_path && name == "SupportedInGameIcons") {
                const icon_thumbnail_path = path.join(editor_asset_path, 'public/editor-assets/InGameIcons');
                return {
                    value: value,
                    label: (
                        <div>
                            <div>{value}</div>
                            <div style={{ height: 64, width: 64, backgroundImage: `url('${icon_thumbnail_path + "/" + value + ".png"}')`, backgroundSize: 'cover', color: 'white' }}></div>
                        </div>
                    )
                };
            }
            else if (name == "SupportedMusicNames" || (name == "SupportedModelNameList" && message.type == "Sound")) {
                return {
                    value: value,
                    label: (
                        <div>
                            <div>{value}</div>
                            <div>
                                <Button style={{ backgroundColor: '#4CAF50' }} onClick={(e) => playAudio(e, value)} title="Play"><MdPlayCircleOutline /></Button>
                                <Button style={{ marginLeft: "8px", backgroundColor: '#555555' }} onClick={(e) => stopAudio(e, value)} title="Stop"><MdPauseCircleOutline /></Button>
                            </div>
                        </div>
                    )
                };
            }
            else {
                return {
                    value: value,
                    label: value
                };
            }
        });
    }



    _getNames(itemSetName, options) {
        const withNull = options && options.withNull;
        const filter = options && options.filter;
        const parentItemName = options && options.parentItemName;
        const modules = this._modules;
        const cache = filter && {};
        const visited = filter && [];
        const names = withNull ? { '': true } : {};
        for (let i = modules.length - 1; i >= 0; i--) {
            for (let name in modules[i].data[itemSetName]) {
                if (names[name] !== undefined) {
                    continue;
                }
                if (filter && !filter(this._getItemWithParent(itemSetName, parentItemName, name, i, visited, cache))) {
                    names[name] = false;
                    continue;
                }
                names[name] = true;
            }
        }

        return Object.keys(names).filter(name => names[name]).sort(itemListSort);
    }

    _getItemListNoCache(itemSetName, parentItemName, typeName) {
        if (itemSetName === 'ui') {
            const result = [];
            for (let name in this._dataEditorData.uiDefinitions) {
                result.push({ name: name, module: 'CURRENT' });
            }
            return result;
        }
        const modules = this._modules;
        const names = {};
        for (let i = modules.length - 1; i >= 0; i--) {
            const module = modules[i];
            const items = module.data[itemSetName];
            for (let name in items) {
                const item = items[name];
                if (names[name]) {
                    if (!names[name].parent && item[parentItemName]) {
                        names[name].parent = item[parentItemName];
                    }
                    if (!names[name].type && typeName && item[typeName + 'Case'] !== 0) {
                        names[name].type = item.descriptor.fields.find(f => f.number == item[typeName + 'Case']).name;
                    }
                    continue;
                }
                names[name] = { name: name, module: modules[i].name, parent: item[parentItemName] };
                if (typeName && item[typeName + 'Case'] !== 0) {
                    names[name].type = item.descriptor.fields.find(f => f.number == item[typeName + 'Case']).name;
                }
            }
        }
        if (typeName) {
            for (let name in names) {
                this._resolveTypeName(names, name);
            }
        }
        return Object.values(names).sort((a, b) => a.name.localeCompare(b.name));
    }

    _resolveTypeName(names, name) {
        const entry = names[name];
        if (typeof entry.type === 'undefined') {
            if (entry.parent) {
                entry.type = this._resolveTypeName(names, entry.parent);
            } else {
                entry.type = '';
            }
        }
        return entry.type;
    }

    /**
     * @typedef Item
     * @property {string} name
     * @property {string} module
     * @property {string} parent
     */

    /**
     * @param {string} itemSetName
     * @param {string} parentItemName
     * @returns {Item[]} A list of items of the specified kind.
     */
    getItemList(itemSetName, parentItemName, typeName) {
        this._cachedItemLists = this._cachedItemLists || {};
        if (this._cachedItemLists[itemSetName]) {
            const cache = this._cachedItemLists[itemSetName];
            if (cache.data !== this._dataEditorData || cache.undoable !== this._undoableDataEditorData) {
                const newResult = this._getItemListNoCache(itemSetName, parentItemName, typeName);
                let same = true;
                if (cache.result.length == newResult.length) {
                    for (let i in cache.result) {
                        const a = cache.result[i];
                        const b = newResult[i];
                        if (a.name !== b.name || a.module !== b.module || a.parent !== b.parent || a.type !== b.type) {
                            same = false;
                            break;
                        }
                    }
                } else {
                    same = false;
                }
                const updated = same ? cache.result : newResult;
                this._cachedItemLists[itemSetName] = {
                    data: this._dataEditorData,
                    undoable: this._undoableDataEditorData,
                    result: updated,
                };
            }
        } else {
            this._cachedItemLists[itemSetName] = {
                data: this._dataEditorData,
                undoable: this._undoableDataEditorData,
                result: this._getItemListNoCache(itemSetName, parentItemName, typeName),
            };
        }
        return this._cachedItemLists[itemSetName].result;
    }

    _getYieldGroupNames(message) {
        // console.log('search yield group for: ', message);
        const itemSetName = 'spawnWaves';
        const modules = this._modules;
        const cache = {};
        const visited = [];
        const names = {};
        for (let i = modules.length - 1; i >= 0; i--) {
            for (let name in modules[i].data[itemSetName]) {
                if (names[name] !== undefined) {
                    continue;
                }
                /**
                 * @type {SpawnWave}
                 */
                var spawnWave = this._getItemWithParent(itemSetName, null, name, i, visited, cache);
                for (let lane of spawnWave.spawnLanes) {
                    let onThisLane = false;
                    for (let subWave of lane.subWaves) {
                        if (subWave.selfValue === message.selfValue) {
                            onThisLane = true;
                            break;
                        }
                    }
                    if (onThisLane) {
                        // console.log('found the subwave on lane', lane);
                        const result = [];
                        for (let subWave of lane.subWaves) {
                            if (subWave.selfValue !== message.selfValue && subWave.yieldGroup.indexOf(message.name) === -1 &&
                                message.yieldGroup.indexOf(subWave.name) === -1) {
                                result.push(subWave.name);
                            } else {
                                // console.log('skip subwave ', subWave, message, ' because ', subWave.selfValue !== message.selfValue, subWave.yieldGroup.indexOf(message.name)
                                //     , message.yieldGroup.indexOf(subWave.name));
                            }
                        }
                        result.sort();
                        // console.log('subwave yield group result', result);
                        return result;
                    }
                }

            }
        }
        console.log('not found search yield group for: ', message);
        return [];
    }

    _getSupportedModelNames(message) {
        let result = actorResourceNames[message.type];
        if (!result) {
            result = [""];
        }
        return result;
    }

    _updateMessageReferences(message, dataPath, itemSetName, oldName, newName, actions) {
        // console.log('updating', message, message.descriptor);
        const descriptor = message.descriptor;
        for (let field of descriptor.fields) {
            dataPath.push(field.name);
            if (field.isMap) {
                if (field.valueProtoType == FieldDescriptor.TYPE_MESSAGE) {
                    const values = message[field.name];
                    for (let key in values) {
                        dataPath.push(key);
                        this._updateMessageReferences(values[key], dataPath, itemSetName, oldName, newName, actions);
                        dataPath.pop();
                    }
                }
            } else if (field.isRepeated) {
                if (field.protoType == FieldDescriptor.TYPE_MESSAGE) {
                    const values = message[field.name];
                    for (let i = 0; i < values.length; i++) {
                        dataPath.push(i);
                        this._updateMessageReferences(values[i], dataPath, itemSetName, oldName, newName, actions);
                        dataPath.pop();
                    }
                } else if (field.protoType == FieldDescriptor.TYPE_STRING) {
                    if (field.options.valueDropdown) {
                        const info = this._supportedValueDropdowns[field.options.valueDropdown];
                        if (info && typeof info !== 'function') {
                            if (info.field === itemSetName) {
                                const values = message[field.name];
                                for (let i = 0; i < values.length; i++) {
                                    dataPath.push(i);
                                    if (values[i] === oldName) {
                                        actions.push({ type: 'SET', path: dataPath.slice(), value: newName });
                                    }
                                    dataPath.pop();
                                }
                            }
                        }
                    }
                }
            } else {
                let fieldIsPresent = false;
                if (field.oneofIndex !== undefined) {
                    fieldIsPresent = message[descriptor.oneofs[field.oneofIndex].name + 'Case'] === field.number;
                } else {
                    fieldIsPresent = message['has' + field.Name];
                }
                if (fieldIsPresent) {
                    if (field.protoType == FieldDescriptor.TYPE_MESSAGE) {
                        this._updateMessageReferences(message[field.name], dataPath, itemSetName, oldName, newName, actions);
                    } else if (field.protoType == FieldDescriptor.TYPE_STRING) {
                        if (field.options.valueDropdown) {
                            const info = this._supportedValueDropdowns[field.options.valueDropdown];
                            if (info && typeof info !== 'function') {
                                if (info.field === itemSetName && message[field.name] === oldName) {
                                    actions.push({ type: 'SET', path: dataPath.slice(), value: newName });
                                }
                            }
                        }
                    }
                }
            }
            dataPath.pop();
        }
    }

    rename(itemSetName, oldName, newName) {
        const actions = [];
        const dataPath = ['undoableDataEditorData', 'data'];
        const data = this._undoableDataEditorData.data;

        if (!data[itemSetName][oldName]) {
            return { type: 'BATCH', actions };
        }

        // Update all references to this field.
        for (let field of data.descriptor.fields) {
            if (field.isMap) {
                dataPath.push(field.name);
                const items = data[field.name];
                for (let name in items) {
                    dataPath.push(name);
                    this._updateMessageReferences(items[name], dataPath, itemSetName, oldName, newName, actions);
                    dataPath.pop();
                }
                dataPath.pop();
            }
        }

        actions.push({ type: 'SET', path: dataPath.concat([itemSetName, newName]), value: data[itemSetName][oldName].toJson() });
        actions.push({ type: 'DELETE', path: dataPath.concat([itemSetName, oldName]) });

        console.log('rename actions', actions);

        return { type: 'BATCH', actions };
    }
}

export class Arrow extends React.PureComponent {
    render() {
        return (
            <span className="arrow" onClick={this.props.onClick}>
                {
                    this.props.type === 'right' &&
                    <MdPlayArrow />
                }
                {
                    this.props.type === 'down' &&
                    <MdPlayArrow className="rotated" />
                }
            </span>
        );
    }
}

Arrow.propTypes = {
    type: PropTypes.oneOf(['right', 'down']),
    onClick: PropTypes.func,
};

class WikiPopup extends React.PureComponent {
    state = { open: false, wikiContent: null };

    componentDidMount() {
        fetchWikiContent(this.props.wikiPath).then(wikiContent => this.setState({ wikiContent }));
    }

    render() {
        return <Tooltip tooltip={<HelpOutlineIcon fontSize="small" />}>
            <React.Fragment>
                {
                    this.state.wikiContent &&
                    <ReactMarkdown
                        source={this.state.wikiContent}
                        skipHtml={true}
                        transformLinkUri={() => { return null; }}  // disable links in markdown content
                    >
                    </ReactMarkdown>
                }
                {
                    !this.state.wikiContent &&
                    <RefreshIcon fontSize="large" classes={{ root: 'sider-icon-rotate' }} />
                }
            </React.Fragment>
        </Tooltip>;
    }
}

WikiPopup.propTypes = {
    wikiPath: PropTypes.array,
};

export class Row extends React.PureComponent {
    render() {
        if (this.props.draggableId) {
            return (
                <Draggable draggableId={this.props.draggableId} index={this.props.draggableIndex}>
                    {
                        (provided, snapshot) => (
                            <tr className={"mouse_hover " + (this.props.inherent ? 'inherented' : 'ownvalue') + (snapshot.isDragging ? ' dragging' : '')}
                                onContextMenu={this.props.onContextMenu}
                                title={this.props.title}
                                {...provided.draggableProps} ref={provided.innerRef}>
                                <td {...provided.dragHandleProps}><Arrow />{this.props.label}:</td>
                                <td onClick={this.props.onClickValue}>
                                    {this.props.children}
                                </td>
                            </tr>
                        )
                    }
                </Draggable>
            );
        } else {
            return (
                <tr className={"mouse_hover " + (this.props.inherent ? 'inherented' : 'ownvalue')}
                    onContextMenu={this.props.onContextMenu}
                >
                    <td>
                        {this.props.label} {this.props.wikiPath && <WikiPopup wikiPath={this.props.wikiPath} />}
                    </td>

                    <td onClick={this.props.onClickValue}>
                        {this.props.children}
                    </td>
                </tr>
            );

        }
    }
}

Row.propTypes = {
    context: PropTypes.any,
    inherent: PropTypes.bool,
    label: PropTypes.any,
    title: PropTypes.string,
    onContextMenu: PropTypes.func,
    onClickValue: PropTypes.func,
    children: PropTypes.any,
    draggableId: PropTypes.string,
    draggableIndex: PropTypes.number,
    comments: PropTypes.string,
    wikiPath: PropTypes.array,
};

export class FieldRow extends React.PureComponent {
    onContextMenu = e => {
        e.preventDefault();
        e.stopPropagation();
        const id = this.props.context && this.props.context.contextMenuId || 'form-context-menu';
        console.log('show menu with id', id, this.props);
        contextMenu.show({
            id: id,
            event: e,
            props: this.props
        });
    }

    render() {
        // <td onContextMenu={this.onContextMenu}>{this.props.path}:</td>
        if (this.props.noTrTd) {
            return <div title={this.props.field.options.comments} onClick={this.props.onClickValue}>
                {this.props.children}
            </div>;
        }

        const label = this.props.label || (this.props.field.isRepeated ? this.props.index : prettyProtoName(this.props.field.protoName));

        return <Row inherent={this.props.inherent}
            onContextMenu={this.onContextMenu}
            onClickValue={this.props.onClickValue}
            title={this.props.field.options.comments}
            wikiPath={this.props.wikiPath}
            label={label}
            draggableId={this.props.context.supportDragDrop && this.props.field.isRepeated && this.props.dispatchPath ? this.props.dispatchPath.join('-') : ''}
            draggableIndex={this.props.field.isRepeated && this.props.dispatchPath ? this.props.dispatchPath[this.props.dispatchPath.length - 1] : -1}
        >
            {this.props.children}
        </Row>;
    }
}

FieldRow.propTypes = {
    context: PropTypes.any,
    label: PropTypes.string,
    dispatchPath: PropTypes.array,
    field: PropTypes.object,
    index: PropTypes.number,
    inherent: PropTypes.bool,
    noTrTd: PropTypes.bool,
    children: PropTypes.any,
    onClickValue: PropTypes.func,
    wikiPath: PropTypes.array,
};

export class NumberInput extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {};

        this.inputProps = this.props.inputProps || {};
    }

    static getDerivedStateFromProps(props, state) {
        // console.log('number props change', props, state);
        if (props.value === state.fromValue) {
            return null;
        } else if (props.value === state.reportedValue) {
            return { fromValue: props.value };
        } else {
            return { fromValue: props.value, value: props.value, reportedValue: props.value };
        }
    }

    onChangeValue = e => {
        const value = e.target.value;
        const numberValue = parseFloat(value);
        if (isNaN(numberValue)) {
            this.setState({ value, invalid: true });
        } else {
            this.setState({ value, reportedValue: numberValue, invalid: false });
            this.props.onChange(numberValue);
        }
    }

    render() {
        // console.log('render number', this.state.value);
        return <input
            type="text"
            readOnly={!!this.props.readonly}
            value={this.state.value}
            style={{ width: '100%', height: '100%', paddingLeft: '8px' }}
            onChange={this.onChangeValue}
            {...this.inputProps}
        />;
    }
}

NumberInput.propTypes = {
    value: PropTypes.number,
    readonly: PropTypes.bool,
    onChange: PropTypes.func,
    inputProps: PropTypes.object,
};

export class IntFieldEditor extends React.PureComponent {
    onChangeValue = value => {
        if (typeof value !== 'number') {
            throw `Field ${this.props.field.name} expects number but got ${typeof value}`;
        }
        value = value | 0;
        if (value !== this.props.value) {
            this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
        }
    }

    onClearField = () => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath });
    }

    render() {
        if (this.props.field.customEditor) {
            const Editor = this.props.field.customEditor;
            return <FieldRow {...this.props}>
                <Editor field={this.props.field} readonly={this.props.readonly} value={this.props.value} onChange={this.onChangeValue} />
            </FieldRow>;
        }

        return <FieldRow {...this.props}>
            <NumberInput readonly={this.props.readonly} value={this.props.value} onChange={this.onChangeValue} />
        </FieldRow>;
    }
}

IntFieldEditor.propTypes = {
    field: PropTypes.object,
    value: PropTypes.number,
    readonly: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

export class DoubleFieldEditor extends React.PureComponent {
    onChangeValue = value => {
        if (typeof value !== 'number') {
            throw `Field ${this.props.field.name} expects number but got ${typeof value}`;
        }
        if (value !== this.props.value) {
            this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
        }
    }

    onClearField = () => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath });
    }

    render() {
        if (this.props.field.customEditor) {
            const Editor = this.props.field.customEditor;
            return <FieldRow {...this.props}>
                <Editor field={this.props.field} readonly={this.props.readonly} value={this.props.value} onChange={this.onChangeValue} />
            </FieldRow>;
        }
        return <FieldRow {...this.props}>
            <NumberInput readonly={this.props.readonly} value={this.props.value} onChange={this.onChangeValue} />
        </FieldRow>;
    }
}

DoubleFieldEditor.propTypes = {
    field: PropTypes.object,
    value: PropTypes.number,
    readonly: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

export class StringFieldEditor extends React.PureComponent {
    constructor(props) {
        super(props);

        this.valueSelectors = memoizeOne(value => ({ value, label: value || '[None]' }));
    }
    onChangeValue = e => {
        const value = e.target.value;
        this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
    }

    onChangeRealValue = value => {
        if (typeof value !== 'string') {
            throw `Field ${this.props.field.name} expects string but got ${typeof string}`;
        }
        this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
    }

    onChangeSelectValue = item => {
        const value = item.value;
        this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
    }

    onClearField = () => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath });
    }

    getOptions = () => {
        return this.props.context.getValueDropdownOptionsWithLabel(
            this.props.field.options.valueDropdown, this.props.message, this.props.field, this.props.index);
    }

    navigateTo = () => {
        const itemSetName = this.props.context.getValueDropdownItemSetName(this.props.field.options.valueDropdown);
        this.props.context.navigationCallback(itemSetName, this.props.value);
    }

    render() {
        if (this.props.field.customEditor) {
            const Editor = this.props.field.customEditor;
            return <FieldRow {...this.props}>
                <Editor {...this.props} onChange={this.onChangeRealValue} />
            </FieldRow>;
        }
        if (this.props.field.options && this.props.field.options.valueDropdown) {
            if (!this.props.context.hasValueDropdownOptions || this.props.context.hasValueDropdownOptions(this.props.field.options.valueDropdown)) {
                const itemSetName = this.props.context.getValueDropdownItemSetName && this.props.context.getValueDropdownItemSetName(this.props.field.options.valueDropdown);
                return <FieldRow {...this.props}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ flexGrow: 1 }}>
                            <LazySelect readonly={this.props.readonly} value={this.valueSelectors(this.props.value)} onChange={this.onChangeSelectValue} getOptions={this.getOptions} />
                        </div>
                        {
                            this.props.value && itemSetName && this.props.context.navigationCallback &&
                            <Button onClick={this.navigateTo}>Go</Button>
                        }
                    </div>
                </FieldRow>;
            }
        }
        return <FieldRow {...this.props}>
            <input
                style={{ width: '100%', height: '100%', paddingLeft: '8px' }}
                readOnly={!!this.props.readonly} type="text" value={this.props.value} onChange={this.onChangeValue} />
        </FieldRow >;
    }
}

StringFieldEditor.propTypes = {
    context: PropTypes.any,
    message: PropTypes.object,
    field: PropTypes.object,
    index: PropTypes.number,
    value: PropTypes.string,
    readonly: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

export class BoolFieldEditor extends React.PureComponent {
    onClearField = () => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath });
    }

    onClickCheckBox = () => {
        const value = !this.props.value;
        this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
    }

    render() {
        return <FieldRow {...this.props} onClickValue={this.props.readonly ? null : this.onClickCheckBox}>
            <input type="checkbox" label={this.props.field.name} checked={!!this.props.value} readOnly={true} />
        </FieldRow>;
    }
}

BoolFieldEditor.propTypes = {
    context: PropTypes.any,
    field: PropTypes.object,
    value: PropTypes.bool,
    readonly: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

export class EnumFieldEditor extends React.PureComponent {
    onChangeValue = e => {
        const value = parseInt(e.value);
        if (!isNaN(value)) {
            this.props.dispatch({ type: 'SET', path: this.props.dispatchPath, value });
        }
    }

    onClearField = () => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath });
    }

    render() {
        const options = [];
        let selectedName = '<Unknown>';
        const defaultValue = this.props.field.options.default;
        for (let name in this.props.field.type.values) {
            const value = this.props.field.type.values[name];
            // If an explicit default value is specified, don't show the default NONE enum.
            if (defaultValue && value === 0) continue;
            options.push({ label: name, value });
            if (value == this.props.value) {
                selectedName = name;
            }
        }
        if (defaultValue && this.props.value === 0) {
            selectedName = defaultValue;
        }
        return <FieldRow {...this.props}>
            <LazySelect readonly={!!this.props.readonly} value={{ label: selectedName, value: this.props.value }} options={options} onChange={this.onChangeValue} />
        </FieldRow>;
    }
}

EnumFieldEditor.propTypes = {
    context: PropTypes.any,
    field: PropTypes.object,
    value: PropTypes.number,
    readonly: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

class UnknownFieldEditor extends React.PureComponent {
    render() {
        return <tr style={{ backgroundColor: 'red' }}><td>{this.props.field.name}</td><td>{this.props.value}</td></tr>;
    }
}

UnknownFieldEditor.propTypes = {
    field: PropTypes.object,
    value: PropTypes.number,
};

class ListFieldEditor extends React.PureComponent {
    constructor(props) {
        super(props);

        this.protoTypeToEditor = {
            [FieldDescriptor.TYPE_ENUM]: EnumFieldEditor,
            [FieldDescriptor.TYPE_INT32]: IntFieldEditor,
            [FieldDescriptor.TYPE_FLOAT]: DoubleFieldEditor,
            [FieldDescriptor.TYPE_DOUBLE]: DoubleFieldEditor,
            [FieldDescriptor.TYPE_BOOL]: BoolFieldEditor,
            [FieldDescriptor.TYPE_STRING]: StringFieldEditor,
        };

        this.state = {
            hide: {},
        };
    }

    static getDerivedStateFromProps(props, state) {
        // console.log('props', props);
        if (!state.cached_dispatch_paths || props.dispatchPath !== state.cached_dispatch_paths.base
            || props.message !== state.cached_dispatch_paths.message) {
            const cached_dispatch_paths = { base: props.dispatchPath, message: props.message };
            const values = props.message[props.field.name];
            const inherentValues = props.message['inherent' + props.field.Name];
            if (props.dispatchPath) {
                for (let key = 0; key < values.length; key++) {
                    if (key < inherentValues.length) {
                        continue;
                    }
                    const oldPath = state.cached_dispatch_paths ? state.cached_dispatch_paths[key] : null;
                    cached_dispatch_paths[key] = assignIfDifferent(oldPath, props.dispatchPath.concat([key - inherentValues.length]));
                }
            }
            return { cached_dispatch_paths };
        }
        return null;
    }

    getEditorForProtoType(type) {
        const editor = this.protoTypeToEditor[type];
        if (editor) {
            return editor;
        }
        return UnknownFieldEditor;
    }

    onAdd = () => {
        this.props.dispatch({ type: 'ADD', path: this.props.dispatchPath, value: this.props.field.type.default });
    }

    onDelete = i => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath.concat([i]) });
    }

    onHide(i) {
        this.setState({ hide: { ...this.state.hide, [i]: true } });
    }

    onShow(i) {
        this.setState({ hide: { ...this.state.hide, [i]: false } });
    }

    onToggle(i) {
        this.setState({ hide: { ...this.state.hide, [i]: !this.state.hide[i] } });
    }

    onContextMenu = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        const id = this.props.context && this.props.context.contextMenuId || 'form-context-menu';
        console.log('show menu with id', id, this.props);
        contextMenu.show({
            id: id,
            event: e,
            props: {
                dispatchPath: this.props.dispatchPath.concat(index),
                message: this.props.message,
                field: this.props.field,
                index: index,
            },
        });
    }

    render() {
        const path = this.props.path;
        const descriptor = this.props.field;
        const message = this.props.message;
        const values = message[descriptor.name];
        const list_is_inherent = this.props.inherent || message['self' + descriptor.Name].length == 0;
        const readonly = !!this.props.readonly;
        const elements = [];
        const start = descriptor.options.noParent ? message['inherent' + descriptor.Name].length : 0;
        for (let i = start; i < values.length; i++) {
            const value = values[i];
            const field_path = path ? path + '.' + i : i;
            const field_inherent_length = message['inherent' + descriptor.Name].length;
            const field_is_inherent = this.props.inherent || i < field_inherent_length;
            const field_is_readonly = readonly || field_is_inherent;
            const field_dispatch_path = field_is_readonly ? null : this.state.cached_dispatch_paths[i];
            const label = (i - start).toString();
            if (descriptor.protoType == FieldDescriptor.TYPE_MESSAGE) {
                if (this.props.context.supportDragDrop && !field_is_readonly) {
                    elements.push(
                        <Draggable draggableId={field_dispatch_path.join('-')} key={i} index={i - field_inherent_length}>
                            {
                                (provided, snapshot) => (
                                    <tr className={(list_is_inherent ? 'inherented' : 'ownvalue') + (snapshot.isDragging ? ' dragging' : '')} {...provided.draggableProps} ref={provided.innerRef}>
                                        <td colSpan="2">
                                            <div className="mouse_hover" onContextMenu={(e) => this.onContextMenu(e, i)} onClick={() => this.onToggle(i)}>
                                                <Arrow type={this.state.hide[i] ? 'right' : 'down'} />
                                                <span {...provided.dragHandleProps}>
                                                    {label}:
                                                </span>
                                                {!field_is_readonly && <Button style={{ marginLeft: '8px' }} variant="danger" onClick={() => this.onDelete(i)} title="Delete list item"><MdClear /></Button>}
                                            </div>
                                            {
                                                !this.state.hide[i] &&
                                                <div style={{ padding: '0 16px' }}>
                                                    <table className="proto_table">
                                                        {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                                                        <tbody>
                                                            <ProtoEditor readonly={field_is_readonly} key={i} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} data={value} />
                                                        </tbody>
                                                    </table>
                                                </div>
                                            }
                                        </td>
                                    </tr>

                                )
                            }
                        </Draggable>
                    );

                } else {
                    elements.push(
                        <tr key={i + '.all'} className={(list_is_inherent ? 'inherented' : 'ownvalue')}>
                            <td colSpan="2">
                                <div className="mouse_hover" onContextMenu={(e) => this.onContextMenu(e, i)} onClick={() => this.onToggle(i)}>
                                    <Arrow type={this.state.hide[i] ? 'right' : 'down'} />{label}:
                                {!field_is_readonly && <Button style={{ marginLeft: '8px' }} variant="danger" onClick={() => this.onDelete(i)} title="Delete list item"><MdClear /></Button>}
                                </div>
                                {
                                    !this.state.hide[i] &&
                                    <div style={{ padding: '0 16px' }}>
                                        <table className="proto_table">
                                            {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                                            <tbody>
                                                <ProtoEditor readonly={field_is_readonly} key={i} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} data={value} />
                                            </tbody>
                                        </table>
                                    </div>
                                }
                            </td>
                        </tr>);
                }
            } else {
                if (this.props.filter && this.props.filter.path && field_path.toLowerCase().indexOf(this.props.filter.path) === -1 &&
                    value.toString().indexOf(this.props.filter.path) === -1) {
                    continue;
                }
                const FieldEditor = this.getEditorForProtoType(descriptor.protoType);
                elements.push(
                    <FieldEditor
                        readonly={field_is_readonly}
                        key={i}
                        context={this.props.context}
                        dispatch={this.props.dispatch}
                        dispatchPath={field_dispatch_path}
                        inherent={field_is_inherent}
                        path={field_path}
                        message={message}
                        field={descriptor}
                        index={i}
                        label={label}
                        value={value}
                    />
                );
            }
        }
        if (this.props.filter && elements.length == 0) {
            if (this.props.filter.path && path.toLowerCase().indexOf(this.props.filter.path) === -1) {
                return <React.Fragment />;
            }
        }
        return <React.Fragment>
            {elements}
        </React.Fragment>;
    }
}

ListFieldEditor.propTypes = {
    context: PropTypes.any,
    message: PropTypes.object,
    field: PropTypes.object,
    path: PropTypes.string,
    filter: PropTypes.object,
    readonly: PropTypes.bool,
    inherent: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

const getTableProps = () => {
    return {
        style: { overflow: 'visible' }
    };
};

class MyTbodyComponent extends React.Component {
    render() {
        const props = { ...this.props };
        const dispatchPath = props.dispatchPath;
        delete props.dispatchPath;

        if (!dispatchPath) {
            return <ReactTableDefaults.TbodyComponent {...props} />;
        }
        const droppableId = dispatchPath.join('-');
        return (
            <Droppable droppableId={droppableId} type={droppableId}>
                {
                    (provided, snapshot) => {
                        return <div {...provided.droppableProps} ref={provided.innerRef}>
                            <div className={(snapshot.isDraggingOver ? "dragging-over" : "")}>
                                <ReactTableDefaults.TbodyComponent {...props} />
                                {provided.placeholder}
                            </div>
                        </div>;
                    }
                }
            </Droppable >
        );
    }
}

MyTbodyComponent.propTypes = {
    dispatchPath: PropTypes.any,
};

class MyTrComponent extends React.Component {
    render() {
        const props = { ...this.props };
        const dispatchPath = props.dispatchPath;
        delete props.dispatchPath;

        if (!dispatchPath) {
            return <ReactTableDefaults.TrComponent {...props} />;
        }

        const i = dispatchPath[dispatchPath.length - 1];
        return (
            <Draggable key={i} draggableId={"key" + i} index={i}>
                {
                    (provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <div className={(snapshot.isDragging ? "dragging" : "")} >
                                <ReactTableDefaults.TrComponent {...props} />
                            </div>
                        </div>
                    )
                }
            </Draggable>
        );
    }
}

MyTrComponent.propTypes = {
    dispatchPath: PropTypes.any,
};

class MessageListFieldTableEditor extends React.PureComponent {
    protoTypeToEditor = {
        [FieldDescriptor.TYPE_ENUM]: EnumFieldEditor,
        [FieldDescriptor.TYPE_INT32]: IntFieldEditor,
        [FieldDescriptor.TYPE_FLOAT]: DoubleFieldEditor,
        [FieldDescriptor.TYPE_DOUBLE]: DoubleFieldEditor,
        [FieldDescriptor.TYPE_BOOL]: BoolFieldEditor,
        [FieldDescriptor.TYPE_STRING]: StringFieldEditor,
    };

    constructor(props) {
        super(props);

        this.state = {
            inline: false,
            collapse: this.props.context.alwaysCollapse,
            hide: {},
            expanded: {},
        };

        this.columnSelector = memoizeOne(() => this.getColumns());
    }

    getEditorForProtoType(type) {
        const editor = this.protoTypeToEditor[type];
        if (editor) {
            return editor;
        }
        return UnknownFieldEditor;
    }

    hasSubComponent() {
        const descriptor = this.props.field.type.descriptor;
        let fieldCount = 0;
        let hasMapOrOneof = false;
        for (let field of descriptor.fields) {
            if (field.options.hideInInspector) {
                continue;
            }
            if (field.oneofIndex !== undefined) {
                hasMapOrOneof = true;
                continue;
            } else if (field.isMap) {
                hasMapOrOneof = true;
                continue;
            }
            fieldCount++;
        }
        return fieldCount > 0 && hasMapOrOneof;
    }

    static getMessageWidth(proto) {
        if (proto.messageWidth !== undefined) {
            return proto.messageWidth;
        }
        proto.messageWidth = 0;

        let result = 300;
        for (let field of proto.descriptor.fields) {
            if (field.options.hideInInspector) {
                continue;
            }
            if (field.isMap) {
                if (field.valueProtoType === FieldDescriptor.TYPE_MESSAGE) {
                    field.messageWidth = MessageListFieldTableEditor.getMessageWidth(field.valueType);
                } else {
                    field.messageWidth = 300;
                }
            } else if (field.isRepeated) {
                if (field.protoType === FieldDescriptor.TYPE_MESSAGE) {
                    field.messageWidth = MessageListFieldTableEditor.updateColumnWidth(field.type);
                } else {
                    field.messageWidth = 300;
                }
            } else if (field.protoType === FieldDescriptor.TYPE_MESSAGE) {
                field.messageWidth = MessageListFieldTableEditor.getMessageWidth(field.type);
            } else {
                field.messageWidth = 300;
            }
            result = Math.max(result, field.messageWidth);
        }
        proto.messageWidth = result;

        return result;
    }

    static updateColumnWidth(proto) {
        if (proto.columnWidth !== undefined) {
            return proto.columnWidth;
        }
        // If a proto is nested inside itself, use 0 for the second time.
        proto.columnWidth = 0;

        let result = 50 + 180;

        let fieldCount = 0;
        for (let field of proto.descriptor.fields) {
            if (field.options.hideInInspector) {
                continue;
            }
            if (field.oneofIndex !== undefined) {
                // Don't know how to handle oneofs.
                continue;
            } else if (field.isMap) {
                // Don't know how to handle maps.
                continue;
            } else if (field.isRepeated) {
                // Repeated fields.
                if (field.protoType === FieldDescriptor.TYPE_MESSAGE) {
                    field.columnWidth = MessageListFieldTableEditor.updateColumnWidth(field.type) + 32;
                } else {
                    field.columnWidth = 300;
                }
            } else if (field.protoType === FieldDescriptor.TYPE_MESSAGE) {
                field.columnWidth = MessageListFieldTableEditor.getMessageWidth(field.type) + 10;
            } else {
                field.columnWidth = field.type.default === 0 ? 100 : 150;
            }
            fieldCount++;
            result += field.columnWidth;
        }
        if (fieldCount === 0) {
            result += MessageListFieldTableEditor.getMessageWidth(proto) + 20;
        }

        proto.columnWidth = result;

        return result;
    }

    getColumns() {
        const columns = [];
        columns.push({
            id: "#",
            Header: "#",
            Cell: row => (<div>{row.index}</div>),
            width: 50,
        });

        MessageListFieldTableEditor.updateColumnWidth(this.props.field.type);
        const descriptor = this.props.field.type.descriptor;
        let fieldCount = 0;
        for (let field of descriptor.fields) {
            if (field.options.hideInInspector) {
                continue;
            }
            if (field.oneofIndex !== undefined) {
                // Don't know how to handle oneofs.
                continue;
            } else if (field.isMap) {
                // Don't know how to handle maps.
                continue;
            } else if (field.isRepeated) {
                // Repeated fields.
                columns.push({
                    id: field.name,
                    Header: prettyProtoName(field.protoName),
                    Cell: row => {
                        const inherent_length = this.props.message['inherent' + this.props.field.Name].length;
                        const field_is_inherent = this.props.inherent || row.index < inherent_length;
                        const field_is_readonly = this.props.readonly || field_is_inherent;
                        const field_dispatch_path = field_is_readonly ? null : this.props.dispatchPath.concat([row.index - inherent_length, field.name]);
                        const field_path = this.props.path + '.' + row.index + '.' + field.name;
                        return (
                            <table className="proto_table">
                                {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                                <tbody>
                                    <ListFieldDispatcher readonly={field_is_readonly} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} message={row.original} field={field} />
                                </tbody>
                            </table>
                        );
                    },
                    width: field.columnWidth,
                });
            } else if (field.protoType === FieldDescriptor.TYPE_MESSAGE) {
                // Message fields.
                columns.push({
                    id: field.name,
                    Header: prettyProtoName(field.protoName),
                    Cell: row => {
                        const inherent_length = this.props.message['inherent' + this.props.field.Name].length;
                        const field_value = row.original[field.name];
                        const field_is_inherent = this.props.inherent || row.index < inherent_length;
                        const field_is_readonly = this.props.readonly || field_is_inherent;
                        const field_dispatch_path = field_is_readonly ? null : this.props.dispatchPath.concat([row.index - inherent_length, field.name]);
                        const field_path = this.props.path + '.' + row.index + '.' + field.name;
                        return (
                            <table className="proto_table">
                                {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                                <tbody>
                                    <ProtoEditor readonly={field_is_readonly} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} data={field_value} />
                                </tbody>
                            </table>
                        );
                    },
                    width: field.columnWidth,
                });
            } else {
                // Primitive fields.
                columns.push({
                    id: field.name,
                    Header: prettyProtoName(field.protoName),
                    Cell: row => {
                        const FieldEditor = this.getEditorForProtoType(field.protoType);
                        const inherent_length = this.props.message['inherent' + this.props.field.Name].length;
                        const field_value = row.original[field.name];
                        const field_is_inherent = this.props.inherent || row.index < inherent_length;
                        const field_is_readonly = this.props.readonly || field_is_inherent;
                        const field_dispatch_path = field_is_readonly ? null : this.props.dispatchPath.concat([row.index - inherent_length, field.name]);
                        const field_path = this.props.path + '.' + row.index + '.' + field.name;
                        return (
                            <FieldEditor
                                readonly={field_is_readonly}
                                context={this.props.context}
                                dispatch={this.props.dispatch}
                                dispatchPath={field_dispatch_path}
                                inherent={field_is_inherent}
                                path={field_path}
                                message={row.original}
                                field={field}
                                value={field_value}
                                noTrTd={true}
                            />
                        );
                    },
                    width: field.columnWidth,
                });
            }
            fieldCount++;
        }

        if (fieldCount === 0) {
            columns.push({
                id: '##',
                Header: '',
                Cell: row => {
                    const inherent_length = this.props.message['inherent' + this.props.field.Name].length;
                    const field_value = row.original;
                    const field_is_inherent = this.props.inherent || row.index < inherent_length;
                    const field_is_readonly = this.props.readonly || field_is_inherent;
                    const field_dispatch_path = field_is_readonly ? null : this.props.dispatchPath.concat([row.index - inherent_length]);
                    const field_path = this.props.path + '.' + row.index;
                    return (
                        <table className="proto_table">
                            {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                            <tbody>
                                <ProtoEditor readonly={field_is_readonly} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} data={field_value} />
                            </tbody>
                        </table>
                    );
                },
                width: MessageListFieldTableEditor.getMessageWidth(this.props.field.type) + 20,
            });
        }

        if (!this.props.readonly) {
            columns.push(
                {
                    Header: () => (
                        <Button variant="primary" onClick={this.onAdd}>+</Button>
                    ),
                    Cell: row => {
                        const readonly_length = this.props.message['inherent' + this.props.field.Name].length;
                        const row_is_readonly = this.props.readonly || this.props.inherent || row.index < readonly_length;
                        return (
                            <div>
                                <Button variant="primary" onClick={() => this.onDuplicate(row.index)} title="Duplicate (add to list end)">
                                    <MdContentCopy />
                                </Button>
                                {
                                    !row_is_readonly &&
                                    <Button variant="danger" onClick={() => this.onDelete(row.index - readonly_length)} title="Delete">
                                        <MdClear />
                                    </Button>
                                }
                            </div>
                        );
                    },
                    sortable: false,
                    width: 80,
                }
            );
        }

        return columns;
    }

    createSubComponent = row => {
        const inherent_length = this.props.message['inherent' + this.props.field.Name].length;
        const field_value = row.original;
        const field_is_inherent = this.props.inherent || row.index < inherent_length;
        const field_is_readonly = this.props.readonly || field_is_inherent;
        const field_dispatch_path = field_is_readonly ? null : this.props.dispatchPath.concat([row.index - inherent_length]);
        const field_path = this.props.path + '.' + row.index;
        return (<div>
            <table className="proto_table">
                {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                <tbody>
                    <ProtoEditor
                        readonly={field_is_readonly}
                        filter={this.props.filter}
                        context={this.props.context}
                        dispatch={this.props.dispatch}
                        dispatchPath={field_dispatch_path}
                        inherent={field_is_inherent}
                        path={field_path}
                        data={field_value} />
                </tbody>
            </table>
        </div>);
    }

    onAdd = () => {
        this.props.dispatch({ type: 'ADD', path: this.props.dispatchPath, value: this.props.field.type.default });
    }

    onDelete = i => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath.concat([i]) });
    }

    onInsert = start => {
        const selfValues = this.props.message['self' + this.props.field.Name];
        const actions = [];
        for (let i = start + 1; i < selfValues.length; i++) {
            actions.push({ type: 'SET', path: this.props.dispatchPath.concat([i]), value: selfValues[i - 1].selfValue.toJson() });
        }
        actions.push({ type: 'ADD', path: this.props.dispatchPath, value: selfValues[selfValues.length - 1].selfValue.toJson() });
        this.props.dispatch({ type: 'BATCH', actions });
    }

    onDuplicate = i => {
        this.props.dispatch({ type: 'ADD', path: this.props.dispatchPath, value: this.props.message[this.props.field.name][i].selfValue.toJson() });
    }

    onMoveUp = i => {
        const actions = [];
        actions.push({ type: 'SET', path: this.props.dispatchPath.concat([i]), value: this.props.message['self' + this.props.field.Name][i - 1].selfValue.toJson() });
        actions.push({ type: 'SET', path: this.props.dispatchPath.concat([i - 1]), value: this.props.message['self' + this.props.field.Name][i].selfValue.toJson() });
        this.props.dispatch({ type: 'BATCH', actions });
    }

    onMoveDown = i => {
        const actions = [];
        actions.push({ type: 'SET', path: this.props.dispatchPath.concat([i]), value: this.props.message['self' + this.props.field.Name][i + 1].selfValue.toJson() });
        actions.push({ type: 'SET', path: this.props.dispatchPath.concat([i + 1]), value: this.props.message['self' + this.props.field.Name][i].selfValue.toJson() });
        this.props.dispatch({ type: 'BATCH', actions });
    }

    onCollapse = () => {
        this.setState({ collapse: true });
    }

    onExpand = () => {
        this.setState({ collapse: false });
    }

    onHide(i) {
        this.setState({ hide: { ...this.state.hide, [i]: true } });
    }

    onShow(i) {
        this.setState({ hide: { ...this.state.hide, [i]: false } });
    }

    onExpandedChange = expanded => {
        this.setState({ expanded });
    }

    getTdProps = (state, rowInfo, column, table) => {
        if (!rowInfo) {
            // Pad rows will have null rowInfo.
            return {};
        }
        const inherentLength = this.props.message['inherent' + this.props.field.Name].length;
        const dispatchPath = !this.props.inherent && !this.props.readonly && this.props.dispatchPath &&
            rowInfo.index >= inherentLength && this.props.dispatchPath.concat(rowInfo.index - inherentLength);
        return {
            style: { overflow: 'visible' },
            onContextMenu: (e) => {
                console.log('onContextMenu', rowInfo, column, table);
                e.preventDefault();
                e.stopPropagation();
                const id = this.props.context && this.props.context.contextMenuId || 'form-context-menu';
                var props = {
                    dispatchPath,
                    message: this.props.message,
                    field: this.props.field,
                    data: rowInfo.original,
                    index: rowInfo.index,
                };

                if (column.id !== "#") {
                    props.dispatchPath = props.dispatchPath && props.dispatchPath.concat(column.id);
                    props.id = column.id;
                }

                contextMenu.show({
                    id: id,
                    event: e,
                    props: props
                });
            }
        };
    };

    getTrProps = (state, rowInfo, column, table) => {
        if (!rowInfo) {
            // Pad rows will have null rowInfo.
            return {};
        }
        const inherentLength = this.props.message['inherent' + this.props.field.Name].length;
        const dispatchPath = !this.props.inherent && !this.props.readonly && this.props.dispatchPath &&
            rowInfo.index >= inherentLength && this.props.dispatchPath.concat(rowInfo.index - inherentLength);
        return {
            style: { overflow: 'visible' },
            onContextMenu: (e) => {
                console.log('onContextMenu', rowInfo, table);
                e.preventDefault();
                e.stopPropagation();
                const id = this.props.context && this.props.context.contextMenuId || 'form-context-menu';
                contextMenu.show({
                    id: id,
                    event: e,
                    props: {
                        dispatchPath,
                        message: this.props.message,
                        field: this.props.field,
                        data: rowInfo.original,
                        index: rowInfo.index,
                    },
                });
            },
            dispatchPath,
        };
    };

    getTbodyProps = () => {
        return {
            style: { overflow: 'visible' },
            dispatchPath: this.props.dispatchPath,
        };
    }

    render() {
        const columns = this.columnSelector(this.props.field, this.props.readonly);
        const data = this.props.message[this.props.field.name];
        return (
            <div>
                {
                    !this.props.collapse &&
                    <ReactTable
                        data={data}
                        columns={columns}
                        pageSize={data.length}
                        showPagination={false}
                        SubComponent={this.hasSubComponent() ? this.createSubComponent : null}
                        expanded={this.state.expanded}
                        onExpandedChange={this.onExpandedChange}
                        getTableProps={getTableProps}
                        getTbodyProps={this.getTbodyProps}
                        getTdProps={this.getTdProps}
                        getTrProps={this.getTrProps}
                        TrComponent={MyTrComponent}
                        TbodyComponent={MyTbodyComponent}
                    />
                }
            </div>
        );
    }
}

MessageListFieldTableEditor.propTypes = {
    context: PropTypes.any,
    message: PropTypes.object,
    field: PropTypes.object,
    path: PropTypes.string,
    filter: PropTypes.object,
    readonly: PropTypes.bool,
    collapse: PropTypes.bool,
    inherent: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

class MapFieldEditor extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            adding: false,
            defaultName: '',
        };

        this.protoTypeToEditor = {
            [FieldDescriptor.TYPE_ENUM]: EnumFieldEditor,
            [FieldDescriptor.TYPE_INT32]: IntFieldEditor,
            [FieldDescriptor.TYPE_FLOAT]: DoubleFieldEditor,
            [FieldDescriptor.TYPE_DOUBLE]: DoubleFieldEditor,
            [FieldDescriptor.TYPE_BOOL]: BoolFieldEditor,
            [FieldDescriptor.TYPE_STRING]: StringFieldEditor,
        };
    }

    static getDerivedStateFromProps(props, state) {
        // console.log('props', props);
        if (!state.cached_dispatch_paths || props.dispatchPath !== state.cached_dispatch_paths.base
            || props.message !== state.cached_dispatch_paths.message) {
            const cached_dispatch_paths = { base: props.dispatchPath, message: props.message };
            if (props.dispatchPath) {
                const values = props.message[props.field.name];
                const selfValues = props.message['self' + props.field.Name];
                for (let key in values) {
                    if (selfValues[key] === undefined) {
                        continue;
                    }
                    const oldPath = state.cached_dispatch_paths ? state.cached_dispatch_paths[key] : null;
                    cached_dispatch_paths[key] = assignIfDifferent(oldPath, props.dispatchPath.concat([key]));
                }
            }
            return { cached_dispatch_paths };
        }
        return null;
    }

    getEditorForProtoType(type) {
        const editor = this.protoTypeToEditor[type];
        if (editor) {
            return editor;
        }
        return UnknownFieldEditor;
    }

    onAdd = () => {
        this.setState({ adding: true });
    }

    cancelAdd = () => {
        this.setState({ adding: false });
    }

    confirmAdd = () => {
        this.setState({ adding: false });

        this.props.dispatch({ type: 'SET', path: this.props.dispatchPath.concat([this.state.addingName]), value: this.props.field.valueType.default });
    }

    onChangeName = e => {
        this.setState({ addingName: e.target.value });
    }

    onDelete = i => {
        this.props.dispatch({ type: 'DELETE', path: this.props.dispatchPath.concat([i]) });
    }

    render() {
        const path = this.props.path;
        const descriptor = this.props.field;
        const message = this.props.message;
        const values = message[descriptor.name];
        const readonly = !!this.props.readonly;
        const elements = [];
        for (let key in values) {
            const value = values[key];
            const field_path = path ? path + '.' + key : key;
            const field_is_inherent = this.props.inherent || message['self' + descriptor.Name][key] === undefined;
            const field_is_readonly = readonly || field_is_inherent;
            const field_dispatch_path = field_is_readonly ? null : this.state.cached_dispatch_paths[key];
            if (descriptor.valueProtoType == FieldDescriptor.TYPE_MESSAGE) {
                elements.push(
                    <tr key={key + '-all'} className={field_is_inherent ? 'inherented' : 'ownvalue'}>
                        <td>{field_path}:</td>
                        <td>{!field_is_readonly && <Button variant="danger" onClick={() => this.onDelete(key)}>Delete</Button>}</td>
                    </tr>);
                elements.push(<ProtoEditor readonly={field_is_readonly} key={key} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} inherent={field_is_inherent} path={field_path} data={value} />);
            } else {
                if (this.props.filter && this.props.filter.path && field_path.toLowerCase().indexOf(this.props.filter.path) === -1 &&
                    value.toString().indexOf(this.props.filter.path) === -1) {
                    continue;
                }
                const FieldEditor = this.getEditorForProtoType(descriptor.protoType);
                elements.push(
                    <FieldEditor
                        readonly={field_is_readonly}
                        key={key}
                        context={this.props.context}
                        dispatch={this.props.dispatch}
                        dispatchPath={field_dispatch_path}
                        inherent={field_is_inherent}
                        path={field_path}
                        message={message}
                        field={descriptor}
                        value={value}
                    />
                );
            }
        }
        const map_is_inherent = this.props.inherent || Object.keys(message['self' + descriptor.Name]).length == 0;
        return <React.Fragment>
            <tr><td>
                <div className={map_is_inherent ? 'inherented' : 'ownvalue'}>{path}:</div>
            </td>
                <td>
                    {!readonly &&
                        <Button onClick={this.onAdd}>Add</Button>
                    }
                    <Modal show={this.state.adding}
                        size="lg"
                        aria-labelledby="contained-modal-title-vcenter"
                        centered>
                        <Modal.Header>
                            <Modal.Title>Adding {descriptor.name}</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            Name: <input defaultValue={this.state.defaultName} onChange={this.onChangeName}></input>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button variant="primary" onClick={this.confirmAdd}>OK</Button>
                        </Modal.Footer>
                    </Modal>
                </td>
            </tr>
            {elements}
        </React.Fragment>;
    }
}

MapFieldEditor.propTypes = {
    context: PropTypes.any,
    message: PropTypes.object,
    field: PropTypes.object,
    path: PropTypes.string,
    filter: PropTypes.object,
    readonly: PropTypes.bool,
    inherent: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
};

export class GroupHeader extends React.PureComponent {
    state = {
        collapse: false,
    };

    onToggle = () => {
        if (this.props.noCollapse) {
            return;
        }
        if (this.props.length > 0) {
            this.setState({ collapse: !this.state.collapse });
        }
    }

    render() {
        return (
            <React.Fragment>
                <tr
                    onContextMenu={this.props.onContextMenu}
                    onClick={this.onToggle} className={'mouse_hover ' + (this.props.inherent ? 'inherented' : 'ownvalue')}>
                    <td colSpan="2">
                        <div>
                            <Arrow type={this.state.collapse ? 'right' : 'down'} />
                            <span style={{ marginRight: '8px' }}>{this.props.label}</span>
                            {this.state.collapse && ` (${this.props.length} elements folded)`}
                            {!this.props.readonly && <Button style={{ marginLeft: '8px' }} onClick={this.props.onAdd} title="Add element">+</Button>}
                        </div>
                    </td>
                </tr>
                {
                    this.props.length > 0 && !this.state.collapse && this.props.children
                }
            </React.Fragment>
        );
    }
}

GroupHeader.propTypes = {
    readonly: PropTypes.bool,
    inherent: PropTypes.bool,
    label: PropTypes.string,
    onContextMenu: PropTypes.func,
    onAdd: PropTypes.func,
    length: PropTypes.number,
    children: PropTypes.any,
    noCollapse: PropTypes.bool,
};

export class ListFieldDispatcher extends React.PureComponent {
    state = {
        inline: false,
        collapse: this.props.context.popupLists || this.props.context.alwaysCollapse,
        showPopup: false,
        // We set title before getting title from wiki, so it can fallback when no internet
        wikiContent: this.props.field.options.comments || ""
    };

    static isEmpty(props) {
        if (props.field.options.noParent) {
            return props.message['self' + props.field.Name].length === 0;
        } else {
            return props.message[props.field.name].length === 0;
        }
    }

    static getDerivedStateFromProps(props, state) {
        if (ListFieldDispatcher.isEmpty(props) && !state.collapse) {
            return { collapse: true };
        }
        return null;
    }

    onContextMenu = e => {
        e.preventDefault();
        e.stopPropagation();
        const id = this.props.context && this.props.context.contextMenuId || 'form-context-menu';
        console.log('show menu with id', id, this.props);
        contextMenu.show({
            id: id,
            event: e,
            props: this.props
        });
    }

    onAdd = e => {
        e.preventDefault();
        e.stopPropagation();
        if (this.props.field.protoType == FieldDescriptor.TYPE_MESSAGE) {
            this.props.dispatch({ type: 'ADD', path: this.props.dispatchPath, value: {} });
        } else {
            this.props.dispatch({ type: 'ADD', path: this.props.dispatchPath, value: this.props.field.type.default });
        }
    }

    onAddAndExpand = e => {
        this.onAdd(e);
        this.setState({ collapse: false });
    }

    onExpand = () => {
        this.setState({ collapse: false });
    }

    onCollapse = () => {
        this.setState({ collapse: true });
    }

    onToggle = () => {
        this.setState({ collapse: !this.state.collapse });
    }

    onShowPopup = () => {
        console.log('show popup');
        this.setState({ showPopup: true });
    }

    onHidePopup = () => {
        this.setState({ showPopup: false });
    }

    renderContent() {
        const field_descriptor = this.props.field;
        if (field_descriptor.customEditor !== null) {
            const CustomEditor = field_descriptor.customEditor;
            return <CustomEditor {...this.props} />;
        }
        const useTable = field_descriptor.protoType == FieldDescriptor.TYPE_MESSAGE && MessageListFieldTableEditor.updateColumnWidth(this.props.field.type) < 1920 && this.props.context.supportDragDrop;

        if (useTable) {
            return (
                <tr>
                    <td colSpan={2} style={{ paddingLeft: '32px' }}>
                        <MessageListFieldTableEditor {...this.props} />
                    </td>
                </tr>
            );
        }
        if (this.props.context.supportDragDrop && this.props.dispatchPath) {
            return (
                <tr>
                    <td colSpan={2} style={{ padding: '0 16px' }}>
                        <Droppable droppableId={this.props.dispatchPath.join('-')} type={this.props.dispatchPath.join('-')}>
                            {
                                (provided, snapshot) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        <table className={"proto_table" + (snapshot.isDraggingOver ? ' dragging-over' : '')} >
                                            <tbody>
                                                <ListFieldEditor {...this.props} />
                                                {provided.placeholder}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            }
                        </Droppable>
                    </td>
                </tr>
            );
        }
        return (
            <tr>
                <td colSpan={2} style={{ padding: '0 16px' }}>
                    <table className="proto_table">
                        <tbody>
                            <ListFieldEditor {...this.props} />
                        </tbody>
                    </table>
                </td>
            </tr>
        );
    }

    renderInternal() {
        const field_descriptor = this.props.field;
        const hasSelfValue = this.props.message['self' + field_descriptor.Name].length > 0;

        return (
            <GroupHeader
                onContextMenu={this.onContextMenu}
                inherent={!hasSelfValue || this.props.inherent}
                readonly={this.props.readonly}
                length={this.props.message[field_descriptor.name].length}
                label={prettyProtoName(field_descriptor.protoName)}
                onAdd={this.onAdd}
                noCollapse={this.props.context.noCollapse}
            >
                {
                    this.renderContent()
                }
            </GroupHeader>
        );
    }

    renderBrief() {
        const field_descriptor = this.props.field;
        const values = field_descriptor.options.noParent ? this.props.message['self' + field_descriptor.Name] : this.props.message[field_descriptor.name];
        const parts = [];
        for (let value of values) {
            if (typeof value === 'object') {
                parts.push(simpleFormat(value.selfValue));
            } else {
                parts.push(value.toString());
            }
        }
        return '[' + parts.join(',') + ']';
    }

    render() {
        if (!this.props.context.popupLists || this.props.field.options.noPopup) {
            return this.renderInternal();
        }
        const field_descriptor = this.props.field;
        const empty = ListFieldDispatcher.isEmpty(this.props);
        const hasSelfValue = !empty && this.props.message['self' + field_descriptor.Name].length > 0;
        return (
            <React.Fragment>
                <tr
                    onContextMenu={this.onContextMenu}
                    className={'mouse_hover ' + (!hasSelfValue || this.props.inherent || this.props.readonly ? 'inherented' : 'ownvalue')}>
                    <td onClick={empty ? null : this.onToggle}>
                        <div>
                            <Arrow type={this.state.collapse ? 'right' : 'down'} />
                            <span style={{ marginRight: '8px' }}>{prettyProtoName(field_descriptor.protoName)}{this.props.wikiPath && <WikiPopup wikiPath={this.props.wikiPath} />}</span>
                            {!this.props.readonly && <Button style={{ marginLeft: '8px' }} onClick={this.onAddAndExpand} title="Add element">+</Button>}
                        </div>
                    </td>
                    <td>
                        {
                            this.state.showPopup &&
                            <Modal show={this.state.showPopup} onHide={this.onHidePopup} size="lg" centered>
                                <Modal.Body>
                                    <table className="proto_table">
                                        <tbody>
                                            {this.renderInternal()}
                                        </tbody>
                                    </table>
                                </Modal.Body>
                            </Modal>
                        }
                        {
                            this.state.collapse &&
                            <div style={{ position: 'relative', height: '100%' }} onClick={this.onShowPopup}>
                                <div style={{ position: 'absolute', left: 0, right: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '25px' }}>
                                    {this.renderBrief()}
                                </div>
                            </div>
                        }
                        {
                            !this.state.collapse &&
                            <Button variant={this.props.readonly ? 'secondary' : 'primary'} onClick={this.onShowPopup}>{this.props.readonly ? 'View' : 'Edit'}</Button>
                        }
                    </td>
                </tr>
                {
                    !empty && !this.state.collapse && this.renderContent()
                }
            </React.Fragment>
        );
    }
}

ListFieldDispatcher.propTypes = {
    context: PropTypes.any,
    message: PropTypes.object,
    field: PropTypes.object,
    readonly: PropTypes.bool,
    inherent: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
    wikiPath: PropTypes.array,
};

class MessageFieldDispatcher extends React.PureComponent {
    state = {
        collapse: this.props.context.alwaysCollapse,

        // We set title before getting title from wiki, so it can fallback when no internet
        wikiContent: this.props.field.options.comments || ""
    };

    onExpand = () => {
        this.setState({ collapse: false });
    }

    onContextMenu = e => {
        e.preventDefault();
        e.stopPropagation();
        const id = this.props.context && this.props.context.contextMenuId || 'form-context-menu';
        console.log('show menu with id', id, this.props);
        contextMenu.show({
            id: id,
            event: e,
            props: this.props
        });
    }

    onToggle = e => {
        if (this.props.context.noCollapse) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.setState({ collapse: !this.state.collapse });
    }

    render() {
        const field_descriptor = this.props.field;
        if (this.props.data.descriptor.fields.length === 0) {
            return (<React.Fragment />);
        }

        return (<React.Fragment>
            <tr>
                <td colSpan={2} className={"mouse_hover " + (this.props.inherent || this.props.data.selfValue.isEmpty() ? 'inherented' : 'ownvalue')}
                    onContextMenu={this.onContextMenu}
                    onClick={this.onToggle}>
                    <Arrow type={this.state.collapse ? 'right' : 'down'} />
                    <span style={{ marginRight: '8px' }}>{prettyProtoName(field_descriptor.protoName)} {this.props.wikiPath && <WikiPopup wikiPath={this.wikiPath} />} </span>
                    {this.state.collapse && <span>{`(${this.props.data.descriptor.fields.length} fields folded)`}</span>}
                </td>
            </tr>
            {
                !this.state.collapse &&
                <tr>
                    <td colSpan={2} style={{ padding: '0 16px' }}>
                        <div style={{ padding: '2px', border: '1px solid #dadada', borderRadius: '4px' }}>
                            <table className="proto_table">
                                {false && <thead><tr><th>Field</th><th>Value</th></tr></thead>}
                                <tbody>
                                    <ProtoEditor {...this.props} />
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            }
        </React.Fragment>);
    }
}

MessageFieldDispatcher.propTypes = {
    context: PropTypes.any,
    field: PropTypes.object,
    data: PropTypes.object,
    inherent: PropTypes.bool,
    readonly: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
    wikiPath: PropTypes.array,
};

/**
 * Generates table rows (i.e., <tr><td>...<td><td>...<td><tr>) for a proto message.
 */
export class ProtoEditor extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            collapse: {}
        };

        this.protoTypeToEditor = {
            [FieldDescriptor.TYPE_ENUM]: EnumFieldEditor,
            [FieldDescriptor.TYPE_INT32]: IntFieldEditor,
            [FieldDescriptor.TYPE_FLOAT]: DoubleFieldEditor,
            [FieldDescriptor.TYPE_DOUBLE]: DoubleFieldEditor,
            [FieldDescriptor.TYPE_BOOL]: BoolFieldEditor,
            [FieldDescriptor.TYPE_STRING]: StringFieldEditor,
        };
    }

    getEditorForProtoType(type) {
        const editor = this.protoTypeToEditor[type];
        if (editor) {
            return editor;
        }
        return UnknownFieldEditor;
    }

    static getDerivedStateFromProps(props, state) {
        // console.log('props', props);
        if (!state.cached_dispatch_paths || props.dispatchPath !== state.cached_dispatch_paths.base
            || props.data.descriptor !== state.cached_dispatch_paths.descriptor) {
            const cached_dispatch_paths = { base: props.dispatchPath, descriptor: props.data.descriptor };
            if (props.dispatchPath) {
                for (let field of props.data.descriptor.fields) {
                    cached_dispatch_paths[field.name] = props.dispatchPath.concat([field.name]);
                }
            }
            return { cached_dispatch_paths };
        }
        return null;
    }

    // // Used to diagnose what's causing a render.
    // componentDidUpdate(prevProps, prevState) {
    //     Object.entries(this.props).forEach(([key, val]) =>
    //         prevProps[key] !== val && console.log(`${this.props.path}@${this.props.data.constructor.name}: prop '${key}' changed`, prevProps[key], val)
    //     );
    //     Object.entries(this.state).forEach(([key, val]) =>
    //         prevState[key] !== val && console.log(`${this.props.path}@${this.props.data.constructor.name}: state '${key}' changed`, prevState[key], val)
    //     );
    // }

    onChangeOneof = (oneof, fieldName) => {
        const field = oneof.fields.find(f => f.name === fieldName);
        // console.log('change oneof', oneof, fieldName, field);
        if (field) {
            if (this.props.data[oneof.name + 'Case'] !== field.number) {
                this.props.dispatch({ type: 'SET', path: this.props.dispatchPath.concat([field.name]), value: field.type.default });
            }
        }
    }

    onExpand(name) {
        this.setState({ collapse: { ...this.state.collapse, [name]: false } });
    }

    onCollapse(name) {
        this.setState({ collapse: { ...this.state.collapse, [name]: true } });
    }

    onInline(name) {
        this.setState({ inline: { ...this.state.inline, [name]: true } });
    }

    onOutline(name) {
        this.setState({ inline: { ...this.state.inline, [name]: false } });
    }

    onAdd(name, value) {
        this.props.dispatch({ type: 'ADD', path: this.props.dispatchPath.concat([name]), value });
    }

    render() {
        const data = this.props.data;
        const path = this.props.path;

        // console.log('render ProtoEditor', path, data.constructor.name);

        const readonly = !!this.props.readonly;

        const elements = [];
        const visitedOneofs = new Set();

        for (let field_descriptor of data.descriptor.fields) {
            if (field_descriptor.options.hideInInspector) {
                continue;
            }
            const field_value = data[field_descriptor.name];
            const field_path = path ? path + '.' + field_descriptor.name : field_descriptor.name;
            const field_dispatch_path = readonly ? null : this.state.cached_dispatch_paths[field_descriptor.name];

            let field_is_inherent = this.props.inherent;
            if (field_descriptor.oneofIndex !== undefined) {
                const oneof = data.descriptor.oneofs[field_descriptor.oneofIndex];

                if (!field_is_inherent && data.selfValue[oneof.name + 'Case'] === 0) {
                    field_is_inherent = true;
                }

                const selectedCase = data[oneof.name + 'Case'];
                if (!visitedOneofs.has(oneof) && (selectedCase === field_descriptor.number || selectedCase === 0)) {
                    visitedOneofs.add(oneof);

                    const oneof_options = oneof.fields.filter(f => this.props.context.view && f.options.view ? this.props.context.view.indexOf(f.options.view) !== -1 : true)
                        .map(f => ({ label: prettyProtoName(f.protoName), value: f.name }));
                    const selected_option = selectedCase === 0 ? { label: '<Invalid>', value: -1 } : { label: prettyProtoName(field_descriptor.protoName), value: field_descriptor.name };
                    elements.push(
                        <tr key={oneof.name} className={field_is_inherent ? 'inherented' : 'ownvalue'}>
                            <td><Arrow />{prettyName(oneof.name)}</td>
                            <td>
                                <LazySelect
                                    className={field_is_inherent ? 'inherented' : 'ownvalue'}
                                    readonly={readonly}
                                    value={selected_option}
                                    options={oneof_options}
                                    onChange={e => this.onChangeOneof(oneof, e.value)} />
                            </td>
                        </tr>
                    );
                }

                if (selectedCase !== field_descriptor.number) {
                    continue;
                }
            } else if (!field_is_inherent && !field_descriptor.isMap && !field_descriptor.isRepeated) {
                if (!data.selfValue['has' + field_descriptor.Name]) {
                    field_is_inherent = true;
                }
            }
            if (field_descriptor.options.hideInInspector) {
                continue;
            }
            if (field_descriptor.options.view && this.props.context.view) {
                if (this.props.context.view.indexOf(field_descriptor.options.view) === -1) {
                    continue;
                }
            }
            if (field_descriptor.isMap) {
                elements.push(<MapFieldEditor readonly={readonly} key={field_descriptor.name} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} message={data} field={field_descriptor} />);
            } else if (field_descriptor.isRepeated) {
                elements.push(<ListFieldDispatcher readonly={readonly} key={field_descriptor.name} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} message={data} field={field_descriptor} />);
            } else if (field_descriptor.protoType == FieldDescriptor.TYPE_MESSAGE) {
                elements.push(<MessageFieldDispatcher readonly={readonly} key={field_descriptor.name} filter={this.props.filter} context={this.props.context} dispatch={this.props.dispatch} dispatchPath={field_dispatch_path} path={field_path} inherent={field_is_inherent} field={field_descriptor} data={field_value} />);
            } else {
                if (this.props.filter && this.props.filter.path && field_path.toLowerCase().indexOf(this.props.filter.path) === -1 &&
                    field_value.toString().indexOf(this.props.filter.path) === -1) {
                    continue;
                }
                const FieldEditor = this.getEditorForProtoType(field_descriptor.protoType);
                const wikiPath = this.props.tabName ? [wikiTitleName(this.props.tabName), wikiTitleNameForField(field_descriptor)] : (this.props.wikiPath ? this.props.wikiPath.concat([wikiTitleNameForField(field_descriptor)]) : null);
                console.log('wikiPath', wikiPath, this.props.tabName, this.props.wikiPath, this.props);
                elements.push(
                    <FieldEditor
                        readonly={readonly}
                        key={field_descriptor.name}
                        context={this.props.context}
                        dispatch={this.props.dispatch}
                        dispatchPath={field_dispatch_path}
                        inherent={field_is_inherent}
                        path={field_path}
                        message={data}
                        field={field_descriptor}
                        value={field_value}
                        wikiPath={wikiPath}
                    />
                );
            }
        }
        return <React.Fragment>{elements}</React.Fragment>;
    }
}

ProtoEditor.propTypes = {
    context: PropTypes.any,
    data: PropTypes.object,
    path: PropTypes.string,
    filter: PropTypes.object,
    readonly: PropTypes.bool,
    inherent: PropTypes.bool,
    dispatch: PropTypes.func,
    dispatchPath: PropTypes.array,
    typeName: PropTypes.string,
    tabName: PropTypes.string,
    wikiPath: PropTypes.string,
};
